// =============================================================
// 🧠 useDashboardMetrics.js — Hook oficial da IA do Amor Dashboard
// =============================================================
//
// O que este hook faz:
// - Escuta TODAS as sessões no Firestore em tempo real
// - Calcula:
//   ✔ KPIs principais
//   ✔ Atendimentos por dia
//   ✔ Atendimentos por horário
//   ✔ Distribuição por avatar
//   ✔ Taxa de resolução automática
//
// - Garante que valores nulos/ausentes não quebrem a dashboard
// - Garante consistência visual mesmo com poucos dados
//
// =============================================================

import { useEffect, useState, useRef } from "react";
import { listenAllSessions } from "../../services/iaDashboard/sessionsService";
import { computeDashboardMetrics } from "../../services/iaDashboard/metricsService";

export default function useDashboardMetrics() {
  // =============================================================
  // STATE PRINCIPAL
  // =============================================================
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [metrics, setMetrics] = useState({
    activeSessions: 0,
    pendingSessions: 0,
    finishedToday: 0,
    avgResponseTimeMinutes: 0,
    avgSessionDurationMinutes: 0,
  });

  const [sessionsByDay, setSessionsByDay] = useState([]);
  const [sessionsByHour, setSessionsByHour] = useState([]);
  const [avatarDistribution, setAvatarDistribution] = useState([]);
  const [autoResolutionRate, setAutoResolutionRate] = useState(0);

  // Evita setState após unmount para não gerar memory leak
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    // =============================================================
    // 🔥 Escuta todas as sessões do Firestore
    // =============================================================
    const unsub = listenAllSessions(
      (list) => {
        if (!mountedRef.current) return;

        try {
          // Segurança: garante que a lista é sempre array
          const safeList = Array.isArray(list) ? list : [];

          setSessions(safeList);

          // =============================================================
          // 🧠 Computa todas as métricas da Dashboard
          // =============================================================
          const {
            metrics,
            sessionsByDay,
            sessionsByHour,
            avatarDistribution,
            autoResolutionRate,
          } = computeDashboardMetrics(safeList);

          setMetrics(metrics);
          setSessionsByDay(sessionsByDay);
          setSessionsByHour(sessionsByHour);
          setAvatarDistribution(avatarDistribution);
          setAutoResolutionRate(autoResolutionRate);

          setLoading(false);
        } catch (err) {
          console.error("Erro ao processar métricas:", err);
          setError(err);
          setLoading(false);
        }
      },

      (err) => {
        if (!mountedRef.current) return;
        console.error("Erro ao ouvir sessões:", err);
        setError(err);
        setLoading(false);
      }
    );

    // Cleanup
    return () => {
      mountedRef.current = false;
      if (typeof unsub === "function") unsub();
    };
  }, []);

  // =============================================================
  // RETORNO
  // =============================================================
  return {
    sessions,
    loading,
    error,
    metrics,
    sessionsByDay,
    sessionsByHour,
    avatarDistribution,
    autoResolutionRate,
  };
}
